import { useEffect, useState } from "react";
import { Send, CheckCircle2, AlertCircle, Linkedin, Calendar, ExternalLink, Mail } from "lucide-react";
import SectionHeading from "./SectionHeading";
import { isFreeEmailDomain } from "../lib/freeEmailDomains";
import { isAwsWafEnabled, loadAwsWafSdk, wafFetch } from "../lib/awsWaf";
import { useT } from "../i18n/useT";

// Fill these in to surface external links in the contact panel.
// Empty string => the link is hidden.
const LINKS = {
  email: "hello@sv4-tech.com",
  linkedin: "", // e.g. "https://www.linkedin.com/in/sergiopiriz/"
  calendly: "", // e.g. "https://calendly.com/sergio-piriz/discovery-call"
  upwork: "",
  malt: "",
};

type Status =
  | { state: "idle" }
  | { state: "sending" }
  | { state: "ok" }
  | { state: "err"; msg: string };

export default function Contact() {
  const t = useT();
  const [status, setStatus] = useState<Status>({ state: "idle" });
  const [emailError, setEmailError] = useState<string | null>(null);

  // Pre-load the AWS WAF JS SDK on mount so that on the first submit
  // the silent challenge has the JS already in memory and the
  // user-perceived latency stays under ~300 ms.
  useEffect(() => {
    if (isAwsWafEnabled()) {
      loadAwsWafSdk().catch(() => {
        // Non-fatal — wafFetch will retry on submit.
      });
    }
  }, []);

  function checkEmail(value: string): string | null {
    const v = value.trim();
    if (!v) return null;
    if (isFreeEmailDomain(v)) return t.contact.form.errors.emailFree;
    return null;
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status.state === "sending") return;

    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form)) as Record<string, string>;

    const emailMsg = checkEmail(data.email ?? "");
    if (emailMsg) {
      setEmailError(emailMsg);
      setStatus({ state: "err", msg: emailMsg });
      return;
    }
    setEmailError(null);

    setStatus({ state: "sending" });
    try {
      const res = await wafFetch("/api/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
        const msg =
          body.error === "rate_limited"
            ? t.contact.form.errors.rateLimited
            : body.error === "personal_email_blocked"
              ? body.message ?? t.contact.form.errors.personalEmail
              : body.error === "invalid_input"
                ? t.contact.form.errors.invalidInput
                : t.contact.form.errors.sendFailed;
        setStatus({ state: "err", msg });
        return;
      }
      form.reset();
      setStatus({ state: "ok" });
    } catch {
      setStatus({ state: "err", msg: t.contact.form.errors.network });
    }
  }

  return (
    <section id="contact" className="py-24 sm:py-28 border-t border-ink-800/60 bg-ink-900/30">
      <div className="container-page grid gap-12 lg:grid-cols-[1fr_1.2fr]">
        <div>
          <SectionHeading eyebrow={t.contact.eyebrow} title={t.contact.title} lede={t.contact.lede} />

          <div className="mt-8 flex flex-wrap gap-3">
            {LINKS.calendly && (
              <a className="btn-primary" href={LINKS.calendly} target="_blank" rel="noreferrer">
                <Calendar className="size-4" />
                {t.contact.callCta}
              </a>
            )}
            {LINKS.email && (
              <a className="btn-ghost" href={`mailto:${LINKS.email}`}>
                <Mail className="size-4" />
                {t.contact.emailCta}
              </a>
            )}
          </div>

          <ul className="mt-10 space-y-3 text-sm">
            {LINKS.email && (
              <li className="flex items-center gap-3 text-ink-300">
                <span className="text-ink-400 w-24 shrink-0">{t.contact.labels.email}</span>
                <a className="text-accent-400 hover:text-accent-500" href={`mailto:${LINKS.email}`}>
                  {LINKS.email}
                </a>
              </li>
            )}
            {LINKS.linkedin && (
              <li className="flex items-center gap-3 text-ink-300">
                <span className="text-ink-400 w-24 shrink-0">{t.contact.labels.linkedin}</span>
                <a
                  className="inline-flex items-center gap-1.5 text-accent-400 hover:text-accent-500"
                  href={LINKS.linkedin}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Linkedin className="size-4" />
                  {t.contact.labels.profile}
                  <ExternalLink className="size-3" />
                </a>
              </li>
            )}
            {(LINKS.upwork || LINKS.malt) && (
              <li className="flex items-center gap-3 text-ink-300">
                <span className="text-ink-400 w-24 shrink-0">{t.contact.labels.freelance}</span>
                <span className="flex flex-wrap gap-3">
                  {LINKS.upwork && (
                    <a
                      className="inline-flex items-center gap-1.5 text-accent-400 hover:text-accent-500"
                      href={LINKS.upwork}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Upwork
                      <ExternalLink className="size-3" />
                    </a>
                  )}
                  {LINKS.malt && (
                    <a
                      className="inline-flex items-center gap-1.5 text-accent-400 hover:text-accent-500"
                      href={LINKS.malt}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Malt
                      <ExternalLink className="size-3" />
                    </a>
                  )}
                </span>
              </li>
            )}
            <li className="flex items-center gap-3 text-ink-300">
              <span className="text-ink-400 w-24 shrink-0">{t.contact.labels.based}</span>
              {t.contact.labels.basedValue}
            </li>
          </ul>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-xl border border-ink-800 bg-ink-900/50 p-6 sm:p-8"
          noValidate
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="name">{t.contact.form.name}</label>
              <input id="name" name="name" required className="field" autoComplete="name" />
            </div>
            <div>
              <label className="label" htmlFor="email">{t.contact.form.email}</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className={`field ${emailError ? "border-rose-500/70 focus:border-rose-500 focus:ring-rose-500/30" : ""}`}
                autoComplete="email"
                onBlur={(e) => setEmailError(checkEmail(e.currentTarget.value))}
                onChange={(e) => {
                  if (emailError) setEmailError(checkEmail(e.currentTarget.value));
                }}
              />
              {emailError && (
                <p className="mt-1.5 text-xs text-rose-400 leading-snug">{emailError}</p>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="label" htmlFor="company">{t.contact.form.company}</label>
              <input id="company" name="company" className="field" autoComplete="organization" />
            </div>
            <div className="sm:col-span-2">
              <label className="label" htmlFor="message">{t.contact.form.message}</label>
              <textarea
                id="message"
                name="message"
                required
                minLength={10}
                rows={6}
                className="field resize-y"
              />
            </div>
            <div aria-hidden className="hidden">
              <label>
                Website
                <input name="website" tabIndex={-1} autoComplete="off" />
              </label>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <button type="submit" className="btn-primary" disabled={status.state === "sending"}>
              {status.state === "sending" ? t.contact.form.sending : t.contact.form.send}
              {status.state !== "sending" && <Send className="size-4" />}
            </button>
            {status.state === "ok" && (
              <p className="flex items-center gap-2 text-sm text-emerald-400">
                <CheckCircle2 className="size-4" />
                {t.contact.form.ok}
              </p>
            )}
            {status.state === "err" && (
              <p className="flex items-center gap-2 text-sm text-rose-400">
                <AlertCircle className="size-4" />
                {status.msg}
              </p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}
